/*
 * @format
 *
 * Selectors over the desired-state slice — OTHER clients' pending control
 * changes, projected by the backend and kept in its own slice keyed by
 * interfaceId (home.id). These complement the app's own client-local pending
 * machinery (pendingSetpoint/Mode/HolidayMode/Schedule): local optimistic
 * state always wins for a zone, and a goal echoing this client's own command
 * is never surfaced on top of it.
 */

import {
  getPendingData,
  hasPendingRequestId,
} from '../reducers/pending-reducer';
import { isMatchingZoneId } from '../utils/helpers';
import { HOLIDAY_MODE } from '../utils/consts';

// The client-minted pending slices whose request ids the server echoes back as
// a goal's clientRequestId. A goal carrying one of these ids is this client's
// own command coming back, not another client's change. Publish is excluded —
// it is not a goal kind, so its request ids never appear in goals/resolved.
const OWN_REQUEST_SLICES = [
  'pendingSetpoint',
  'pendingMode',
  'pendingHolidayMode',
  'pendingSchedule',
];

// True when clientRequestId names a control/schedule command THIS client
// originated. This is the correlation the contract defines: the app mints a
// requestId per command and the server threads it back as clientRequestId on
// both goals and resolved[].
//
// The durable ownDesiredRequests set is the authoritative source — it outlives
// the ephemeral pending-* entries, so an own goal stays recognised after the
// local entry has completed, timed out, or been debounce-cleared, right up
// until the server reports the request terminal. The live pending slices are
// still consulted too (harmless): recognition then also holds for a brand-new
// command before any desired-state push has echoed its id back.
export const isOwnPendingRequestId = (state, clientRequestId) => {
  if (!clientRequestId) {
    return false;
  }

  if (state.ownDesiredRequests && state.ownDesiredRequests[clientRequestId]) {
    return true;
  }

  return OWN_REQUEST_SLICES.some(slice =>
    hasPendingRequestId(state[slice], clientRequestId),
  );
};

// A local control pending entry for the zone means the app already renders its
// own optimistic value there (mirrors getMode/getSetpoint's local overlay), so shared pending
// must stand down. Only the control slices count — schedule is a separate
// surface and does not gate the control render.
const localControlPending = (state, homeId, zoneId) =>
  getPendingData(state.pendingSetpoint, homeId, zoneId) ||
  getPendingData(state.pendingMode, homeId, zoneId) ||
  getPendingData(state.pendingHolidayMode, homeId, zoneId);

// The reported (telemetry) zone from the home slice. Kept local to this module
// — a duplicate of controls-selectors' getZone — so the settling self-heal can
// consult reported state without importing controls-selectors (which imports
// getSharedPending back, a cycle). Reads only the home slice; never mutates.
const reportedZone = (state, homeId, zoneId) => {
  const home =
    state.homeList &&
    Array.isArray(state.homeList.homes) &&
    state.homeList.homes.find(h => h.id === homeId);

  return (
    home &&
    Array.isArray(home.zones) &&
    home.zones.find(z => isMatchingZoneId(z.zoneId, zoneId))
  );
};

// A settling entry is "confirmed" once this phone's own reported state has
// reached it. A mode-only goal (e.g. Auto) carries no setpoint on the wire, so
// mode alone decides the match there; a valued goal must match both.
export const settlingMatchesReported = (zone, settling) =>
  !!zone &&
  zone.mode === settling.mode &&
  (settling.setpoint == null || zone.setpoint === settling.setpoint);

// Whether the interface's reported zone state has caught up to a settling
// entry. Used by the GC saga to decide when to prune; keeps the reported-match
// rule in one place, shared with getSharedPending's self-heal below.
export const reportedMatchesSettling = (state, interfaceId, settling) =>
  settlingMatchesReported(
    reportedZone(state, interfaceId, [settling.prtId, settling.endpointId]),
    settling,
  );

// Holiday is a distinct axis from the heating mode, not a point on it. The
// backend projects it under its own `holiday` kind; older projections may still
// carry it as a control goal with mode 'Holiday'. Recognise both so holiday is
// routed to the holiday representation (isInHolidayMode) and NEVER surfaced as a
// heating mode/setpoint (which rendered "Unknown" and left the zone controllable).
const isHolidayGoal = g =>
  !!g &&
  (g.kind === 'holiday' ||
    (g.kind === 'control' && g.mode === HOLIDAY_MODE));

// LEAVING holiday is its own projected kind, and it carries IDS ONLY — no mode,
// no setpoint, no end time. That is deliberate on the backend's part: leaving
// targets nothing, because the device restores its own mode and setpoint unaided
// (~1.2s) and the values it reports DURING holiday are not the ones it returns
// to. A zone's reported temperatureMode while in holiday is the mode it will
// return to rather than what it is doing, and its reported setpoint is usually
// the frost-protection value; the old projection captured those and replayed
// them as an ordinary control goal, which on one episode drove a 20°C zone to
// 13.5°C. So there is nothing here to overlay onto the heating display, and
// anything this goal is read as claiming about the zone would be invented.
const isHolidayExitGoal = g => !!g && g.kind === 'holiday-exit';

const controlGoalForZone = (goals, zoneId) =>
  Array.isArray(goals)
    ? goals
        .filter(
          g =>
            g &&
            g.kind === 'control' &&
            !isHolidayGoal(g) &&
            g.prtId === zoneId[0] &&
            g.endpointId === zoneId[1],
        )
        .sort((a, b) => (b.requestedAt || 0) - (a.requestedAt || 0))[0]
    : undefined;

const holidayGoalForZone = (goals, zoneId) =>
  Array.isArray(goals)
    ? goals
        .filter(
          g => isHolidayGoal(g) && g.prtId === zoneId[0] && g.endpointId === zoneId[1],
        )
        .sort((a, b) => (b.requestedAt || 0) - (a.requestedAt || 0))[0]
    : undefined;

const holidayExitGoalForZone = (goals, zoneId) =>
  Array.isArray(goals)
    ? goals
        .filter(
          g =>
            isHolidayExitGoal(g) &&
            g.prtId === zoneId[0] &&
            g.endpointId === zoneId[1],
        )
        .sort((a, b) => (b.requestedAt || 0) - (a.requestedAt || 0))[0]
    : undefined;

const settlingForZone = (settling, zoneId) =>
  Array.isArray(settling)
    ? settling
        .filter(
          s => s && s.prtId === zoneId[0] && s.endpointId === zoneId[1],
        )
        .sort((a, b) => (b.requestedAt || 0) - (a.requestedAt || 0))[0]
    : undefined;

// The effective desired-state overlay for a zone: the accepted control target
// the reconciler is currently driving, whether THIS client or another client
// originated it. Shaped for a minimal "pending" render; values are returned
// verbatim (same units as reported state).
//
// Returns null unless: the interface has a desired-state entry; NO local
// optimistic pending is already rendering for the zone (that wins during its
// brief first-render window); and either a live control goal targets the zone
// OR — once the goal has completed — a still-unconfirmed settling entry does.
//
// Own goals ARE surfaced: a client keeps showing its own in-flight goal after
// its short-lived local optimistic entry clears, so the REQUESTING phone holds
// the desired value while the reconciler is still acting on it (retries included),
// matching an observing phone — instead of snapping back to stale reported after
// a few seconds. (Own goals historically stood down here to avoid mislabelling
// them as a peer's change in the old tinted tile badge; B1 removed that framing,
// so the stand-down lapsed. The durable own-request set still drives own-outcome
// recognition in applyResolvedOutcomes — that is unchanged.)
//
// The settling fallback smooths the completion transition: a completed goal is
// gone from goals[] but its accepted value is retained as settling until this
// phone's own reported state matches — then it self-heals to null and reported
// takes over.
export const getSharedPending = (state, homeId, zoneId) => {
  const desired = state.desiredState && state.desiredState[homeId];

  if (!desired || !zoneId) {
    return null;
  }

  // (a) local optimistic value already renders for this zone
  if (localControlPending(state, homeId, zoneId)) {
    return null;
  }

  // most recent control goal targeting this zone
  const goal = controlGoalForZone(desired.goals, zoneId);

  if (goal) {
    return {
      mode: goal.mode,
      setpoint: goal.setpoint,
      holdDuration: goal.holdDuration,
      holidayEndTime: goal.holidayEndTime,
      clientRequestId: goal.clientRequestId,
      requestedAt: goal.requestedAt,
    };
  }

  // no live goal for the zone → fall back to a retained settling entry
  const settling = settlingForZone(desired.settling, zoneId);

  if (!settling) {
    return null;
  }

  // only while reported has NOT yet caught up — once it matches, the display
  // self-heals to null and the reported value (now equal) renders instead
  if (settlingMatchesReported(reportedZone(state, homeId, zoneId), settling)) {
    return null;
  }

  return {
    mode: settling.mode,
    setpoint: settling.setpoint,
    // mirror the live-goal branch so an OverridePlusHours boost keeps its
    // countdown (getPlusHoursEndtime reads holdDuration) through the settle window
    holdDuration: settling.holdDuration,
    holidayEndTime: settling.holidayEndTime,
    clientRequestId: settling.clientRequestId,
    requestedAt: settling.requestedAt,
  };
};

// The shared desired HOLIDAY overlay for a zone: an accepted "into holiday" goal
// the reconciler is currently driving, whether this client or another originated
// it. Holiday is a distinct axis from the heating mode — it is consumed by
// isInHolidayMode (which disables controls and drives the holiday display), NOT
// by getMode/getSetpoint — so it is surfaced here separately from getSharedPending.
//
// The goal is authored against the CH zone (endpoint 5); callers map a DHW zone
// to its CH zone before asking (see isInHolidayMode). Stands down while a local
// optimistic holiday pending renders for the zone, mirroring getSharedPending's
// localControlPending gate. Returns { holidayEndTime, clientRequestId, requestedAt }
// or null.
//
// Entering and leaving are NOT symmetric here. An in-flight leave is its own
// projected kind ('holiday-exit', see getSharedHolidayExitPending) and is
// deliberately not reported by this selector: entering has an accepted target
// worth showing early, whereas leaving has none, and letting a leave REQUEST end
// the holiday display would announce a change that has not happened yet. Only
// reported telemetry (holidayMode) ends holiday for the display.
export const getSharedHolidayPending = (state, homeId, zoneId) => {
  const desired = state.desiredState && state.desiredState[homeId];

  if (!desired || !zoneId) {
    return null;
  }

  if (getPendingData(state.pendingHolidayMode, homeId, zoneId)) {
    return null;
  }

  const goal = holidayGoalForZone(desired.goals, zoneId);

  return goal
    ? {
        holidayEndTime: goal.holidayEndTime,
        clientRequestId: goal.clientRequestId,
        requestedAt: goal.requestedAt,
      }
    : null;
};

// A LEAVE-holiday request in flight for a zone — this client's or another's.
//
// This is a PENDING INDICATOR, NOT A STATE, and the distinction is the whole
// point of the kind. The goal says only "someone has asked this zone to leave
// holiday and the reconciler has accepted it". It does NOT say the zone has
// left, and it cannot say what mode or setpoint the zone is returning to,
// because the request never named one (see isHolidayExitGoal). Whether a zone
// IS in holiday is answered by reported telemetry alone — holidayMode, read via
// isInHolidayMode — and nothing here may pre-empt that.
//
// Keyed on the CH zone the goal is authored against, matching
// getSharedHolidayPending; callers map a DHW zone to its CH zone first. Stands
// down while a local optimistic holiday pending renders for the zone, so the
// requesting phone's own machinery keeps precedence exactly as it does for the
// entering case. Returns { clientRequestId, requestedAt } or null.
export const getSharedHolidayExitPending = (state, homeId, zoneId) => {
  const desired = state.desiredState && state.desiredState[homeId];

  if (!desired || !zoneId) {
    return null;
  }

  if (getPendingData(state.pendingHolidayMode, homeId, zoneId)) {
    return null;
  }

  const goal = holidayExitGoalForZone(desired.goals, zoneId);

  return goal
    ? {
        clientRequestId: goal.clientRequestId,
        requestedAt: goal.requestedAt,
      }
    : null;
};
